import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import BookingDatePicker from '../components/ui/BookingDatePicker';
import {
  Mic2,
  Users,
  Calendar,
  MapPin,
  Clock,
  Music,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  Sparkles,
  PartyPopper,
  Building2,
  Tent,
  Star,
} from 'lucide-react';
import { db } from '../services/firebase';
import { collection, addDoc } from 'firebase/firestore';

// Booking steps
const STEPS = {
  ARTIST: 0,
  EVENT: 1,
  PACKAGE: 2,
  DETAILS: 3,
  CONFIRM: 4,
};

// Artist options
const artists = [
  {
    id: 'scooby',
    name: 'Scooby DaGodSon',
    subtitle: 'Solo Artist',
    description: 'Electrifying solo performances perfect for intimate venues and club nights',
    image: '/images/Scooby.png',
    perfectFor: ['Club nights', 'Private parties', 'Intimate venues', 'Birthday celebrations'],
    startingPrice: 1500,
  },
  {
    id: 'sirius',
    name: 'Sirius Company',
    subtitle: 'Full Band Experience',
    description: 'The complete go-go band experience with full instrumentation and energy',
    image: '/images/sirius-band.png',
    perfectFor: ['Festivals', 'Corporate events', 'Large venues', 'Weddings'],
    startingPrice: 3500,
  },
];

// Event types
const eventTypes = [
  { id: 'private', name: 'Private Party', icon: PartyPopper, description: 'Birthday, anniversary, or private celebration' },
  { id: 'corporate', name: 'Corporate Event', icon: Building2, description: 'Company party, conference, or team event' },
  { id: 'festival', name: 'Festival', icon: Tent, description: 'Music festival or outdoor event' },
  { id: 'club', name: 'Club Night', icon: Music, description: 'Nightclub or bar performance' },
  { id: 'wedding', name: 'Wedding', icon: Sparkles, description: 'Wedding reception or ceremony' },
];

// Packages
const packages = {
  scooby: [
    { id: 'basic', name: 'Essential', duration: '2 hours', price: 1500, features: ['2 sets (45 mins each) with 30 Min Break', 'Standard sound check', 'Meet & greet'] },
    { id: 'standard', name: 'Premium', duration: '3 hours', price: 2200, features: ['2 sets (45 mins each) with 30 Min Break', 'Extended sound check', 'Meet & greet', 'Custom setlist', 'Photo opportunity'] },
    { id: 'premium', name: 'VIP Experience', duration: '4 hours', price: 3000, features: ['2 sets (45 mins each) with 30 Min Break', 'Full production', 'Meet & greet', 'Custom setlist', 'Photo session', 'Exclusive merchandise'] },
    { id: 'custom', name: 'Custom Request', duration: 'Custom', price: 0, features: ['Custom performance duration', 'Tailored setlist', 'Flexible arrangements'], isCustom: true },
  ],
  sirius: [
    { id: 'basic', name: 'Band Essential', duration: '2 hours', price: 3500, features: ['2 sets (45 mins each) with 30 Min Break', 'Full band (5 members)', 'Standard sound'] },
    { id: 'standard', name: 'Band Premium', duration: '3 hours', price: 5000, features: ['2 sets (45 mins each) with 30 Min Break', 'Full band (7 members)', 'Enhanced sound', 'Custom setlist', 'Meet & greet'] },
    { id: 'premium', name: 'Full Production', duration: '4 hours', price: 7500, features: ['2 sets (45 mins each) with 30 Min Break', 'Full band (10+ members)', 'Premium sound & lighting', 'Custom setlist', 'VIP meet & greet', 'Exclusive merchandise'] },
    { id: 'custom', name: 'Custom Request', duration: 'Custom', price: 0, features: ['Custom performance duration', 'Tailored setlist', 'Flexible arrangements'], isCustom: true },
  ],
};

// Progress bar component
function ProgressBar({ currentStep }) {
  const steps = ['Artist', 'Event', 'Package', 'Details', 'Confirm'];
  return (
    <div className="mb-8">
      <div className="flex items-center justify-between relative">
        {/* Progress line */}
        <div className="absolute top-4 left-0 right-0 h-0.5 bg-gray-800">
          <motion.div
            className="h-full bg-gradient-to-r from-orange-500 to-red-600"
            initial={{ width: '0%' }}
            animate={{ width: `${(currentStep / (steps.length - 1)) * 100}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>
        {/* Step indicators */}
        {steps.map((step, index) => (
          <div key={step} className="relative z-10 flex flex-col items-center">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
                index <= currentStep
                  ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white'
                  : 'bg-gray-800 text-gray-500'
              }`}
            >
              {index < currentStep ? <CheckCircle className="w-4 h-4" /> : index + 1}
            </div>
            <span className={`text-xs mt-2 ${index <= currentStep ? 'text-white' : 'text-gray-500'}`}>
              {step}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Book() {
  const [step, setStep] = useState(STEPS.ARTIST);
  const [booking, setBooking] = useState({
    artist: null,
    eventType: null,
    package: null,
    customDuration: '',
    eventDate: '',
    eventTime: '',
    venue: '',
    address: '',
    city: '',
    state: '',
    guestCount: '',
    requiresSound: 'not_sure', // 'yes' | 'no' | 'not_sure'
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const updateBooking = (field, value) => {
    setBooking((prev) => ({ ...prev, [field]: value }));
  };

  const nextStep = () => {
    setStep((prev) => Math.min(prev + 1, STEPS.CONFIRM));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const prevStep = () => {
    setStep((prev) => Math.max(prev - 1, STEPS.ARTIST));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      // Build booking data for Firestore
      const bookingData = {
        // Artist & Package
        artist: booking.artist,
        artistName: selectedArtist?.name,
        package: booking.package,
        packageName: selectedPackage?.name,
        packageDuration: booking.package === 'custom' && booking.customDuration 
          ? `${booking.customDuration} hours` 
          : selectedPackage?.duration,
        customDuration: booking.package === 'custom' ? booking.customDuration : null,

        // Event Details
        eventType: booking.eventType,
        eventDate: booking.eventDate,
        eventTime: (() => {
          if (!booking.eventTime) return '';
          const [hours, minutes] = booking.eventTime.split(':');
          const hour = parseInt(hours);
          const ampm = hour >= 12 ? 'PM' : 'AM';
          const hour12 = hour % 12 || 12;
          return `${hour12}:${minutes} ${ampm}`;
        })(),
        venue: booking.venue,
        address: booking.address,
        city: booking.city,
        state: booking.state,
        guestCount: booking.guestCount,
        requiresSound: booking.requiresSound,

        // Contact Info
        contactName: booking.name,
        contactEmail: booking.email,
        contactPhone: booking.phone,
        message: booking.message,

        // Status & Timestamps
        status: 'pending',
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      // Save to Firestore if configured
      if (db) {
        // Save as booking
        const bookingRef = await addDoc(collection(db, 'bookings'), bookingData);
        
        // Also save as contact entry for admin contacts page
        const soundStatus = booking.requiresSound === 'yes' ? 'Yes - Equipment Needed' :
                           booking.requiresSound === 'no' ? 'No - Venue Provides' : 'Not Sure';
        const contactData = {
          name: booking.name,
          email: booking.email,
          phone: booking.phone,
          category: 'booking',
          message: `Booking Request - ${selectedArtist?.name}\nVenue: ${booking.venue}\nDate: ${booking.eventDate}\nGuests: ${booking.guestCount}\nSound & Backline: ${soundStatus}\n\nAdditional Notes: ${booking.message || 'None'}`,
          read: false,
          createdAt: new Date(),
          bookingId: bookingRef.id,
        };
        await addDoc(collection(db, 'contacts'), contactData);

        // Send automated confirmation email to customer
        try {
          await fetch('/api/send-email', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              to: booking.email,
              subject: `Booking Confirmation - ${selectedArtist?.name}`,
              type: 'booking-confirmation',
              customerName: booking.name,
              artistName: selectedArtist?.name,
              venue: booking.venue,
              eventDate: booking.eventDate,
            }),
          });
        } catch (emailError) {
          console.error('Error sending confirmation email:', emailError);
        }

        // Send notification email to admin team
        try {
          await fetch('/api/send-email', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              to: booking.email, // Used for reply-to
              subject: `New Booking - ${selectedArtist?.name}`,
              type: 'booking-admin-notification',
              customerName: booking.name,
              artistName: selectedArtist?.name,
              venue: booking.venue,
              eventDate: booking.eventDate,
              packageName: selectedPackage?.name,
              packageDuration: booking.package === 'custom' && booking.customDuration
                ? `${booking.customDuration} hours`
                : selectedPackage?.duration,
              eventType: booking.eventType,
              guestCount: booking.guestCount,
              requiresSound: booking.requiresSound,
              phone: booking.phone,
              message: booking.message,
            }),
          });
        } catch (emailError) {
          console.error('Error sending admin notification:', emailError);
        }

        // Send SMS notification if phone is provided
        if (booking.phone) {
          try {
            await fetch('/api/send-sms', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                phoneNumber: booking.phone,
                message: `Hi ${booking.name}! We received your booking request for ${selectedArtist?.name} on ${booking.eventDate}. Check your email for confirmation details. -Scooby DaGodSon`,
                contractId: bookingRef.id,
                clientName: booking.name,
                artistName: selectedArtist?.name,
              }),
            });
          } catch (smsError) {
            console.error('Error sending SMS:', smsError);
          }
        }
      }

      setIsSubmitted(true);
    } catch (error) {
      console.error('Error submitting booking:', error);
      alert('Failed to submit booking. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedArtist = artists.find((a) => a.id === booking.artist);
  const selectedPackages = booking.artist ? packages[booking.artist] : [];
  const selectedPackage = selectedPackages.find((p) => p.id === booking.package);

  const fadeIn = {
    initial: { opacity: 0, x: 20 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -20 },
  };

  // Success state
  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-black text-white pt-32 pb-20 px-6">
        <div className="container mx-auto max-w-2xl">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center"
          >
            <div className="w-20 h-20 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl font-bold mb-4">Booking Request Submitted!</h1>
            <p className="text-gray-400 mb-8 max-w-md mx-auto">
              Thank you for your interest in booking {selectedArtist?.name}. We'll review your request and get back to you within 24-48 hours.
            </p>
            <div className="bg-gray-900 rounded-xl p-6 mb-8 text-left">
              <h3 className="font-semibold mb-4">Booking Summary</h3>
              <div className="space-y-2 text-sm">
                <p><span className="text-gray-500">Artist:</span> {selectedArtist?.name}</p>
                <p><span className="text-gray-500">Package:</span> {selectedPackage?.name}
                  {booking.package === 'custom' && booking.customDuration && ` (${booking.customDuration} hours)`}
                </p>
                <p><span className="text-gray-500">Date:</span> {booking.eventDate}</p>
                <p><span className="text-gray-500">Venue:</span> {booking.venue}</p>
              </div>
            </div>
            <Button
              onClick={() => window.location.href = '/'}
              className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700"
            >
              Return Home
            </Button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white pt-32 pb-20 px-6">
      <div className="container mx-auto max-w-5xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-5xl md:text-6xl font-bold mb-4">
            <span className="text-orange-500">Book</span> Your Event
          </h1>
          <p className="text-xl text-gray-400">
            Bring the DMV go-go experience to your next event
          </p>
        </motion.div>

        {/* Progress Bar */}
        <ProgressBar currentStep={step} />

        {/* Step Content */}
        <AnimatePresence mode="wait">
          {/* Step 1: Artist Selection */}
          {step === STEPS.ARTIST && (
            <motion.div key="artist" {...fadeIn} className="space-y-6">
              <h2 className="text-2xl font-bold text-center mb-8">Who would you like to book?</h2>
              <div className="grid md:grid-cols-2 gap-6">
                {artists.map((artist) => (
                  <Card
                    key={artist.id}
                    className={`cursor-pointer transition-all hover:scale-[1.02] ${
                      booking.artist === artist.id
                        ? 'border-orange-500 ring-2 ring-orange-500/20'
                        : 'hover:border-gray-700'
                    }`}
                    onClick={() => updateBooking('artist', artist.id)}
                  >
                    <CardContent className="p-0">
                      {/* Artist Image */}
                      <div className="h-48 bg-gradient-to-br from-orange-500/20 to-red-600/20 flex items-center justify-center">
                        {artist.id === 'scooby' ? (
                          <Mic2 className="w-20 h-20 text-orange-500" />
                        ) : (
                          <Users className="w-20 h-20 text-orange-500" />
                        )}
                      </div>
                      <div className="p-6">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-xl font-bold">{artist.name}</h3>
                          {booking.artist === artist.id && (
                            <CheckCircle className="w-5 h-5 text-orange-500" />
                          )}
                        </div>
                        <p className="text-orange-500 text-sm font-semibold mb-3">{artist.subtitle}</p>
                        <p className="text-gray-400 text-sm mb-4">{artist.description}</p>
                        <div className="mb-4">
                          <p className="text-xs text-gray-500 mb-2">Perfect for:</p>
                          <div className="flex flex-wrap gap-2">
                            {artist.perfectFor.map((item) => (
                              <span key={item} className="text-xs bg-gray-800 text-gray-300 px-2 py-1 rounded">
                                {item}
                              </span>
                            ))}
                          </div>
                        </div>
                        {/* Removed pricing display */}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              <div className="flex justify-end mt-8">
                <Button
                  onClick={nextStep}
                  disabled={!booking.artist}
                  className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 disabled:opacity-50"
                >
                  Continue <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 2: Event Type */}
          {step === STEPS.EVENT && (
            <motion.div key="event" {...fadeIn} className="space-y-6">
              <h2 className="text-2xl font-bold text-center mb-8">What type of event?</h2>
              <div className="grid md:grid-cols-3 gap-4">
                {eventTypes.map((event) => {
                  const Icon = event.icon;
                  return (
                    <Card
                      key={event.id}
                      className={`cursor-pointer transition-all hover:scale-[1.02] ${
                        booking.eventType === event.id
                          ? 'border-orange-500 ring-2 ring-orange-500/20'
                          : 'hover:border-gray-700'
                      }`}
                      onClick={() => updateBooking('eventType', event.id)}
                    >
                      <CardContent className="p-6 text-center">
                        <div className="w-12 h-12 bg-gradient-to-br from-orange-500/20 to-red-600/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                          <Icon className="w-6 h-6 text-orange-500" />
                        </div>
                        <h3 className="font-semibold mb-1">{event.name}</h3>
                        <p className="text-xs text-gray-400">{event.description}</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
              <div className="flex justify-between mt-8">
                <Button variant="outline" onClick={prevStep} className="border-gray-700">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
                <Button
                  onClick={nextStep}
                  disabled={!booking.eventType}
                  className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 disabled:opacity-50"
                >
                  Continue <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 3: Package Selection */}
          {step === STEPS.PACKAGE && (
            <motion.div key="package" {...fadeIn} className="space-y-6">
              <h2 className="text-2xl font-bold text-center mb-8">Choose your package</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {selectedPackages.map((pkg, index) => (
                  <Card
                    key={pkg.id}
                    className={`cursor-pointer transition-all hover:scale-[1.02] relative ${
                      booking.package === pkg.id
                        ? 'border-orange-500 ring-2 ring-orange-500/20'
                        : 'hover:border-gray-700'
                    } ${index === 1 && !pkg.isCustom ? 'md:-mt-4 md:mb-4' : ''}`}
                    onClick={() => updateBooking('package', pkg.id)}
                  >
                    {index === 1 && !pkg.isCustom && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-orange-500 to-red-600 text-white text-xs font-semibold px-3 py-1 rounded-full flex items-center gap-1">
                        <Star className="w-3 h-3" /> Most Popular
                      </div>
                    )}
                    <CardContent className="p-6">
                      <h3 className="text-xl font-bold mb-1">{pkg.name}</h3>
                      <p className="text-gray-400 text-sm mb-4">
                        {pkg.isCustom ? pkg.duration : `${pkg.duration} performance`}
                      </p>
                      {/* Removed pricing display */}
                      <ul className="space-y-2">
                        {pkg.features.map((feature) => (
                          <li key={feature} className="flex items-center gap-2 text-sm">
                            <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                            <span className="text-gray-300">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                ))}
              </div>
              
              {/* Custom duration input */}
              {booking.package === 'custom' && (
                <Card className="mt-6">
                  <CardContent className="p-6">
                    <h3 className="font-semibold mb-4">Custom Performance Duration</h3>
                    <div className="space-y-2">
                      <Label htmlFor="customDuration">How many hours? (e.g., 1.5, 2, 3)</Label>
                      <Input
                        id="customDuration"
                        type="number"
                        step="0.5"
                        min="1"
                        placeholder="Enter duration in hours"
                        value={booking.customDuration}
                        onChange={(e) => updateBooking('customDuration', e.target.value)}
                      />
                    </div>
                  </CardContent>
                </Card>
              )}
              
              <div className="flex justify-between mt-8">
                <Button variant="outline" onClick={prevStep} className="border-gray-700">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
                <Button
                  onClick={nextStep}
                  disabled={!booking.package || (booking.package === 'custom' && !booking.customDuration)}
                  className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 disabled:opacity-50"
                >
                  Continue <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 4: Event Details */}
          {step === STEPS.DETAILS && (
            <motion.div key="details" {...fadeIn} className="space-y-6">
              <h2 className="text-2xl font-bold text-center mb-8">Event Details</h2>
              <Card>
                <CardContent className="p-6 space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="eventDate">Event Date</Label>
                      <BookingDatePicker
                        value={booking.eventDate}
                        onChange={(date) => updateBooking('eventDate', date)}
                        artistId={booking.artist}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="eventTime">Start Time</Label>
                      <Input
                        id="eventTime"
                        type="time"
                        value={booking.eventTime}
                        onChange={(e) => updateBooking('eventTime', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="venue">Venue Name</Label>
                    <Input
                      id="venue"
                      placeholder="e.g., The Grand Ballroom"
                      value={booking.venue}
                      onChange={(e) => updateBooking('venue', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="address">Street Address</Label>
                    <Input
                      id="address"
                      placeholder="123 Main Street"
                      value={booking.address}
                      onChange={(e) => updateBooking('address', e.target.value)}
                    />
                  </div>
                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="city">City</Label>
                      <Input
                        id="city"
                        placeholder="Washington"
                        value={booking.city}
                        onChange={(e) => updateBooking('city', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="state">State</Label>
                      <Input
                        id="state"
                        placeholder="DC"
                        value={booking.state}
                        onChange={(e) => updateBooking('state', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="guestCount">Expected Guests</Label>
                      <Input
                        id="guestCount"
                        type="number"
                        placeholder="100"
                        value={booking.guestCount}
                        onChange={(e) => updateBooking('guestCount', e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Sound & Backline Section */}
                  <div className="border-t border-gray-800 pt-6">
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                      <Music className="w-5 h-5 text-orange-500" />
                      Sound & Backline Requirements
                    </h3>
                    <div className="space-y-2">
                      <Label>Will you need audio/sound equipment and backline provided?</Label>
                      <div className="grid grid-cols-3 gap-4 mt-2">
                        <button
                          type="button"
                          onClick={() => updateBooking('requiresSound', 'yes')}
                          className={`p-4 rounded-lg border transition-all text-center ${
                            booking.requiresSound === 'yes'
                              ? 'border-orange-500 bg-orange-500/10 text-white'
                              : 'border-gray-700 hover:border-gray-600 text-gray-400'
                          }`}
                        >
                          <span className="font-semibold">Yes</span>
                          <p className="text-xs mt-1 opacity-70">I need equipment</p>
                        </button>
                        <button
                          type="button"
                          onClick={() => updateBooking('requiresSound', 'no')}
                          className={`p-4 rounded-lg border transition-all text-center ${
                            booking.requiresSound === 'no'
                              ? 'border-orange-500 bg-orange-500/10 text-white'
                              : 'border-gray-700 hover:border-gray-600 text-gray-400'
                          }`}
                        >
                          <span className="font-semibold">No</span>
                          <p className="text-xs mt-1 opacity-70">Venue provides</p>
                        </button>
                        <button
                          type="button"
                          onClick={() => updateBooking('requiresSound', 'not_sure')}
                          className={`p-4 rounded-lg border transition-all text-center ${
                            booking.requiresSound === 'not_sure'
                              ? 'border-orange-500 bg-orange-500/10 text-white'
                              : 'border-gray-700 hover:border-gray-600 text-gray-400'
                          }`}
                        >
                          <span className="font-semibold">Not Sure</span>
                          <p className="text-xs mt-1 opacity-70">Need to check</p>
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Includes mixing console, monitors, microphones, DI boxes, drums, keyboards, amps, etc.
                      </p>
                    </div>
                  </div>

                  <div className="border-t border-gray-800 pt-6">
                    <h3 className="font-semibold mb-4">Your Contact Information</h3>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="name">Full Name</Label>
                        <Input
                          id="name"
                          placeholder="John Doe"
                          value={booking.name}
                          onChange={(e) => updateBooking('name', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email</Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="john@example.com"
                          value={booking.email}
                          onChange={(e) => updateBooking('email', e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="mt-4 space-y-2">
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="(202) 555-0123"
                        value={booking.phone}
                        onChange={(e) => updateBooking('phone', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="message">Additional Notes (Optional)</Label>
                    <textarea
                      id="message"
                      rows={4}
                      placeholder="Tell us more about your event, special requests, or any questions. If you've already spoken with our team or have a quote, please include those details here."
                      value={booking.message}
                      onChange={(e) => updateBooking('message', e.target.value)}
                      className="w-full bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                </CardContent>
              </Card>
              <div className="flex justify-between mt-8">
                <Button variant="outline" onClick={prevStep} className="border-gray-700">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
                <Button
                  onClick={nextStep}
                  disabled={!booking.eventDate || !booking.venue || !booking.name || !booking.email || !booking.phone}
                  className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 disabled:opacity-50"
                >
                  Review Booking <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 5: Confirmation */}
          {step === STEPS.CONFIRM && (
            <motion.div key="confirm" {...fadeIn} className="space-y-6">
              <h2 className="text-2xl font-bold text-center mb-8">Review Your Booking</h2>
              <Card>
                <CardContent className="p-6">
                  <div className="grid md:grid-cols-2 gap-8">
                    {/* Left Column - Booking Details */}
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-sm text-gray-500 mb-1">Artist</h3>
                        <p className="text-xl font-bold flex items-center gap-2">
                          {selectedArtist?.id === 'scooby' ? <Mic2 className="w-5 h-5 text-orange-500" /> : <Users className="w-5 h-5 text-orange-500" />}
                          {selectedArtist?.name}
                        </p>
                      </div>
                      <div>
                        <h3 className="text-sm text-gray-500 mb-1">Package</h3>
                        <p className="font-semibold">{selectedPackage?.name}</p>
                        <p className="text-sm text-gray-400">
                          {booking.package === 'custom' && booking.customDuration 
                            ? `${booking.customDuration} hours` 
                            : selectedPackage?.duration}
                        </p>
                      </div>
                      <div>
                        <h3 className="text-sm text-gray-500 mb-1">Event Type</h3>
                        <p className="font-semibold capitalize">{booking.eventType?.replace('_', ' ')}</p>
                      </div>
                      <div>
                        <h3 className="text-sm text-gray-500 mb-1">Date & Time</h3>
                        <p className="font-semibold flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-orange-500" />
                          {(() => { const [y,m,d] = String(booking.eventDate).split('-').map(Number); return new Date(y, m-1, d).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }); })()}
                        </p>
                        <p className="text-sm text-gray-400 flex items-center gap-2 mt-1">
                          <Clock className="w-4 h-4" />
                          {(() => {
                            if (!booking.eventTime) return 'TBD';
                            const [h, m] = booking.eventTime.split(':');
                            const hr = parseInt(h);
                            return `${hr % 12 || 12}:${m} ${hr >= 12 ? 'PM' : 'AM'}`;
                          })()}
                        </p>
                      </div>
                      <div>
                        <h3 className="text-sm text-gray-500 mb-1">Venue</h3>
                        <p className="font-semibold flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-orange-500" />
                          {booking.venue}
                        </p>
                        <p className="text-sm text-gray-400">{booking.address}, {booking.city}, {booking.state}</p>
                      </div>
                      <div>
                        <h3 className="text-sm text-gray-500 mb-1">Sound & Backline</h3>
                        <p className="font-semibold flex items-center gap-2">
                          <Music className="w-4 h-4 text-orange-500" />
                          {booking.requiresSound === 'yes' ? 'Yes - Equipment Needed' :
                           booking.requiresSound === 'no' ? 'No - Venue Provides' :
                           'Not Sure - To Be Determined'}
                        </p>
                      </div>
                    </div>

                    {/* Right Column - Contact Info */}
                    <div>
                      <div className="bg-gray-900 rounded-xl p-6">
                        <h3 className="font-bold mb-2">Your Contact Info</h3>
                        <p className="text-sm text-gray-400">{booking.name}</p>
                        <p className="text-sm text-gray-400">{booking.email}</p>
                        <p className="text-sm text-gray-400">{booking.phone}</p>
                      </div>
                      
                      <div className="mt-6 bg-gray-900 rounded-xl p-6">
                        <h3 className="font-bold mb-2">Next Steps</h3>
                        <p className="text-sm text-gray-400">
                          Once submitted, our team will review your request and send you a detailed quote along with a contract within 24-48 hours.
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="flex justify-between mt-8">
                <Button variant="outline" onClick={prevStep} className="border-gray-700">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700"
                >
                  {isSubmitting ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Submitting...
                    </div>
                  ) : (
                    <>Submit Booking Request</>
                  )}
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
